// Fetches GHL invoices from our serverless endpoint and maps them
// into the Payment shape the dashboard already uses.
//
// GHL invoices are view-only — they get a `_source: "ghl"` flag and
// `_readonly: true` so the UI can hide edit/delete buttons.

import { findClientByEmail } from "./emailUtils";

const ENDPOINT = "/api/ghl-invoices";

const norm = (s) => (s || "").trim().toLowerCase();

// GHL invoice status → dashboard status.
//   paid   → paid
//   sent   → scheduled (or pending if past due)
//   void   → cancelled (treated as pending so it shows but doesn't count)
function mapStatus(ghlStatus, dueDate) {
  const s = (ghlStatus || "").toLowerCase();
  if (s === "paid") return "paid";
  if (s === "void" || s === "cancelled") return "pending";
  if (dueDate) {
    const due = new Date(dueDate);
    if (!Number.isNaN(due.getTime()) && due.getTime() < Date.now()) return "pending";
  }
  return "scheduled";
}

function toISODate(raw) {
  if (!raw) return null;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 10);
}

export async function fetchGhlData() {
  const res = await fetch(ENDPOINT, { headers: { Accept: "application/json" } });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`Sync failed (${res.status}): ${body.slice(0, 160)}`);
  }
  return res.json();
}

// Convert a GHL invoice into a Payment-shaped object the dashboard can render.
export function mapGhlInvoiceToPayment(inv, clients, offers) {
  const email = norm(inv.customerEmail);
  const matchedClient = email ? findClientByEmail(email, clients) : null;
  const firstItemName = inv.items?.[0]?.name || "";
  const matchedOffer = firstItemName
    ? offers.find((o) => norm(o.name) === norm(firstItemName))
    : null;

  const issue = toISODate(inv.issueDate);
  const due = toISODate(inv.dueDate) || issue;
  const status = mapStatus(inv.status, due);

  return {
    id: `ghl:${inv.id}`,
    _source: "ghl",
    _readonly: true,
    _ghlInvoiceNumber: inv.invoiceNumber,
    _ghlCustomerName: inv.customerName,
    _ghlCustomerEmail: inv.customerEmail,
    _ghlCustomerCompany: inv.customerCompany,
    _ghlLineItem: firstItemName,
    clientId: matchedClient?.id || "",
    offerId: matchedOffer?.id || "",
    amount: inv.total || 0,
    amountPaid: inv.amountPaid || 0,
    amountDue: inv.amountDue || 0,
    bookedDate: issue,
    dueDate: due,
    paidDate: status === "paid" ? (toISODate(inv.sentAt) || issue) : null,
    status,
    note: inv.invoiceNumber ? `Invoice ${inv.invoiceNumber}` : "",
  };
}

// Dedupe: if a manual payment shares the same invoice number as a GHL invoice,
// hide the manual one (GHL wins as source of truth).
export function mergeGhlWithManual(manualPayments, ghlPayments) {
  const ghlNumbers = new Set(
    ghlPayments.map((p) => p._ghlInvoiceNumber).filter(Boolean)
  );
  const filteredManual = manualPayments.filter((p) => {
    const num = p._invoiceNumber || p.invoiceNumber;
    return !num || !ghlNumbers.has(num);
  });
  return [...filteredManual, ...ghlPayments];
}

// Apply the user-managed GHL invoice → offer override map to GHL payments.
// Lets the user re-tag synced GHL invoices to a clean offer in their own
// catalog without ever modifying GHL. Manual payments are passed through.
export function applyGhlOfferOverrides(payments, overrideMap) {
  if (!overrideMap || Object.keys(overrideMap).length === 0) return payments;
  return payments.map((p) => {
    if (p._source !== "ghl") return p;
    const num = p._ghlInvoiceNumber;
    if (!num) return p;
    const override = overrideMap[num];
    if (!override) return p;
    return { ...p, offerId: override };
  });
}
